import type { SearchEngineEventItem } from '~/composables/types/searchEngine'
import type { EventDescription } from '~/composables/types/event'
import { useIsSubdomain } from '~/composables/useSubdomain'

const WISHLIST_KEY_BASE = 'konfetti-wishlist' as string

type WishlistItemObject = SearchEngineEventItem | EventDescription

export interface WishlistItem {
  objectId: string
  objectType: 'SearchEngineEventItem' | 'EventDescription'
  object: WishlistItemObject
}

export interface Wishlist {
  items: WishlistItem[]
}

export const getEmptyWishlist = (): Wishlist => ({
  items: [],
})

export const useWishlist = () => {
  const isSubdomain = useIsSubdomain()
  const wishlistLocalStorageKey = () => {
    if (isSubdomain.value) {
      return `${WISHLIST_KEY_BASE}-${isSubdomain.value}`
    }
    return WISHLIST_KEY_BASE
  }

  const { data: wishlist } = useKftLocalStorage<Wishlist>(
    wishlistLocalStorageKey(),
    getEmptyWishlist()
  )

  const isTheSameItem = (
    obj: Partial<WishlistItem>,
    item: Partial<WishlistItem>
  ): boolean => {
    return item.objectId === obj.objectId
  }

  const findItem = (obj: WishlistItem): WishlistItem | undefined => {
    return wishlist.value.items.find((item: WishlistItem) =>
      isTheSameItem(obj, item)
    )
  }

  const isInWishlist = (obj: Partial<WishlistItem>): boolean => {
    return wishlist.value.items.some((item: WishlistItem) =>
      isTheSameItem(item, obj)
    )
  }

  const isSearchEngineEventItemOnWishlist = (
    item: SearchEngineEventItem
  ): boolean => {
    return isInWishlist({
      objectId: searchEngineEventGetters.fnGetHashedId(item),
    })
  }

  const addSearchEngineEventItem = (item: SearchEngineEventItem): void => {
    // Send GTM event no matter if it's already in wishlist
    sendAddToWishlist(item)
    // check if item is already in wishlist
    if (isSearchEngineEventItemOnWishlist(item)) {
      return
    }
    // push new item into cart
    wishlist.value.items.push({
      objectId: searchEngineEventGetters.fnGetHashedId(item),
      objectType: 'SearchEngineEventItem',
      object: item,
    })
  }

  const removeItem = (obj: Partial<WishlistItem>) => {
    if (!import.meta.client) {
      return null
    }

    let result = false

    const startingLength = wishlist.value.items.length

    const toDeleteIdx = wishlist.value.items.findIndex((item) =>
      isTheSameItem(obj, item)
    )

    wishlist.value.items.splice(toDeleteIdx, 1)

    if (startingLength !== wishlist.value.items.length) {
      result = true
    }

    return result
  }

  const removeSearchEngineEventItem = (item: SearchEngineEventItem) => {
    removeItem({ objectId: searchEngineEventGetters.fnGetHashedId(item) })
  }

  return {
    wishlist,
    addSearchEngineEventItem,
    removeItem,
    removeSearchEngineEventItem,
    findItem,
    isInWishlist,
    isSearchEngineEventItemOnWishlist,
  }
}
